import React, { useState } from "react";
import { User, History, LogOut, UserCog, HelpCircle, CreditCard, Coins } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { CreditHistoryModal } from "@/components/CreditHistoryModal";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";

interface ProfileMenuProps {
  onLogout: () => void;
}

export const ProfileMenu = ({ onLogout }: ProfileMenuProps) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { buildPath } = useLocalizedPath();
  const [showCreditHistory, setShowCreditHistory] = useState(false);

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map(n => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="relative h-9 w-9 rounded-full">
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-primary text-primary-foreground">
                {user?.name ? getInitials(user.name) : <User className="h-4 w-4" />}
              </AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56 bg-background" align="end" forceMount>
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium leading-none">{user?.name}</p>
              <p className="text-xs leading-none text-muted-foreground">
                {user?.email}
              </p>
            </div>
          </DropdownMenuLabel>
          {/* Credits display - visible on mobile only */}
          <div className="flex sm:hidden items-center gap-1.5 text-sm px-2 py-2 mx-2 mb-1 rounded-md bg-muted/50">
            <Coins className="h-4 w-4 text-primary" />
            <span className="font-semibold text-foreground">
              {user?.credit_balance || 0} {t('profileMenu.credits') || 'credits'}
            </span>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate(buildPath('/profile'))}>
            <UserCog className="mr-2 h-4 w-4" />
            <span>{t('profileMenu.profile')}</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setShowCreditHistory(true)}>
            <History className="mr-2 h-4 w-4" />
            <span>{t('profileMenu.creditHistory')}</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate(buildPath('/help'))}>
            <HelpCircle className="mr-2 h-4 w-4" />
            <span>{t('profileMenu.help')}</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate(buildPath('/payments'))}>
            <CreditCard className="mr-2 h-4 w-4" />
            <span>{t('profileMenu.payments')}</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onLogout}>
            <LogOut className="mr-2 h-4 w-4" />
            <span>{t('profileMenu.logout')}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <CreditHistoryModal 
        open={showCreditHistory} 
        onOpenChange={setShowCreditHistory} 
      />
    </>
  );
};
